// frontend/src/App.js
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from './axiosConfig';
import 'leaflet/dist/leaflet.css';
import './i18n';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { PreferenceProvider } from './contexts/PreferenceContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { isAdmin, isDriver, isSystemAdmin } from './utils/roleUtils';
import { SystemAnnouncementsProvider, announcementSessionKey } from './contexts/SystemAnnouncementsContext';
import AnnouncementBanner from './components/announcements/AnnouncementBanner';
import AnnouncementInbox from './components/announcements/AnnouncementInbox';
import SystemAnnouncementManager from './components/announcements/SystemAnnouncementManager';
import ServiceAvailabilityNotice from './components/announcements/ServiceAvailabilityNotice';
import { availabilityTitle, ServiceAvailabilityDetails, ServiceAvailabilityStatusLink } from './components/announcements/ServiceAvailabilityContent';
import { useServiceAvailability } from './contexts/ServiceAvailabilityContext';

// Component imports
import './App.css';
import Header from './components/Header';
import Login from './components/Login';
import VehicleList from './components/VehicleList';
import BookingManagement from './components/BookingManagement';
import OperationsPage from './components/OperationsPage';
import AuditLogViewer from './components/AuditLogViewer';
import RouteManagement from './components/RoutesManagement';
import ScheduleManagement from './components/ScheduleManagement';
import TripManagement from './components/TripManagement';
import UserSettings from './components/UserSettings';
import FeedbackSurveys from './components/surveys/FeedbackSurveys';
import PreferenceLoader from './components/PreferenceLoader';
import ForgotPassword from './components/ForgotPassword';
import ResetPasswordFromLink from './components/ResetPasswordFromLink';
import InviteRegistration from './components/InviteRegistration';
import GPSDeviceManagement from './components/GPSDeviceManagement';
import WebSocketNotificationToastContainer from './components/notifications/WebSocketNotificationToastContainer';
import { resolveLegacyUserPreferenceLanguage } from './utils/languageUtils';
import logger from './utils/logger';
import { resolveSelectedEntityLaunchPath } from './utils/launchPagePaths';
import { derivePreferenceScopes } from './utils/preferenceScopes';
import {
  AUTH_CORRELATION,
  handleLogout,
  tokensMatchCorrelation,
} from './utils/authUtils';

const SettingsRoute = ({ authToken, userRole, entities, currentEntity, onEntityPreferenceSuccess }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const surveyToken = searchParams.get('token');

  // Legacy trip-completion emails used /settings/feedback-surveys?token=...
  // before the public token survey route existed. Handle only that exact token
  // link publicly; all other settings URLs keep the normal login requirement.
  if (location.pathname === '/settings/feedback-surveys' && surveyToken) {
    return <FeedbackSurveys />;
  }

  return authToken ? (
    <UserSettings
      userRole={userRole}
      entities={entities}
      currentEntity={currentEntity}
      onEntityPreferenceSuccess={onEntityPreferenceSuccess}
    />
  ) : <Navigate to="/login" />;
};

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [username, setUsername] = useState(localStorage.getItem('username') || '');
  const [userRole, setUserRole] = useState(localStorage.getItem('role') || null);
  const [entityId, setEntityId] = useState(localStorage.getItem('entityId') || '');
  const [entityName, setEntityName] = useState(localStorage.getItem('entityName') || '');
  const [entityLogoUrl, setEntityLogoUrl] = useState(localStorage.getItem('entityLogoUrl') || null);
  const [entities, setEntities] = useState([]);
  const [currentEntity, setCurrentEntity] = useState(null);
  const [globalRouteRole, setGlobalRouteRole] = useState(null);
  const [bootstrapStatus, setBootstrapStatus] = useState(token ? 'pending' : 'idle');
  const bootstrapGenerationRef = useRef(0);
  const bootstrapAttemptRef = useRef(null);
  const availability = useServiceAvailability();
  const { ready, t } = useTranslation();

  const retireBootstrap = useCallback(() => {
    // Retire ownership before aborting: cancellation and late completion cannot
    // publish an error or account data for a cleared/replaced session or unmount.
    bootstrapGenerationRef.current += 1;
    const attempt = bootstrapAttemptRef.current;
    bootstrapAttemptRef.current = null;
    attempt?.controller.abort();
  }, []);
  useEffect(() => () => retireBootstrap(), [retireBootstrap]);

  const clearUserData = useCallback(() => {
    retireBootstrap();
    localStorage.clear(); // or clear specific items
    delete axios.defaults.headers.common.Authorization;
    setToken(null);
    setUsername('');
    setUserRole(null);
    setEntityId('');
    setEntityName('');
    setEntityLogoUrl(null);
    setEntities([]);
    setCurrentEntity(null);
    setGlobalRouteRole(null);
    setBootstrapStatus('idle');
    
  }, [retireBootstrap]);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
    }
  }, []); // Run only once on mount

  const selectRelevantFields = useCallback((entity) => {
    // Entity rows and user-entity rows arrive with slightly different field
    // names depending on the backend join path. Normalize here once so routing,
    // Header display, PreferenceContext, and API callers all use the same
    // entity-scoped shape.
    return {
      entityId: entity.id || entity.entityId,  // Select `id` or `entityId` for primary entity
      entityName: entity.name || entity.entityName,  // Consistent name field
      entity_type: entity.entity_type || entity.type || '',  // Consistent type field
      status: entity.status || 'active',  // Entity status
      entityLogoUrl: entity.logo_url || entity.logoUrl || entity.entityLogoUrl || '',  // Consistent logo URL field logoUrl
      time_zone: entity.time_zone || '',  // Time zone for the entity
      default_launch_page: entity.default_launch_page || '',
      is_primary: entity.is_primary === true || entity.is_primary === 'true' || entity.is_primary === 1,
      address: entity.address || '',
      city: entity.city || '',
      province: entity.province || '',
      postcode: entity.postcode || '',
      country: entity.country || '',
      latitude: entity.latitude || null,
      longitude: entity.longitude || null,
      booking_window_days: entity.booking_window_days || null,
      allow_recurring_bookings: entity.allow_recurring_bookings,
      booking_required_confirmation: entity.booking_required_confirmation,
      eta_source: entity.eta_source,
      membershipRole: entity.membershipRole || entity.role || null,
      hasActiveMembership: entity.hasActiveMembership === true,
      canEditUserEntityPreferences: entity.canEditUserEntityPreferences === true,
      // Global route authority is applied only when App selects an explicitly
      // synthetic catalogue row or All; it is never copied into membership data.
      userRole: entity.membershipRole || entity.role || null,
      // Multi-language settings
      entity_default_language: entity.entity_default_language || 'en',
      multi_language_enabled: entity.multi_language_enabled || false,
      // enable_boarding_tracking: entity.enable_boarding_tracking || false
    };
  }, []);

  

  // Theme is now handled by PreferenceContext

  const fetchUserData = useCallback(() => {
    if (!token) return Promise.resolve();
    const expectedToken = token;
    const current = bootstrapAttemptRef.current;
    if (current && tokensMatchCorrelation(current.expectedToken, expectedToken, AUTH_CORRELATION.VERIFIED_SESSION)
      && tokensMatchCorrelation(expectedToken, localStorage.getItem('token'), AUTH_CORRELATION.VERIFIED_SESSION)) return current.promise;
    retireBootstrap();
    const generation = bootstrapGenerationRef.current;
    const attempt = { expectedToken, generation, controller: new AbortController(), promise: null };
    // Register synchronously, so repeated activation coalesces even before React
    // renders the pending button. This ref owns a request, not rendered UI state.
    bootstrapAttemptRef.current = attempt;
    setBootstrapStatus('pending');

    const isCurrentAttempt = () => (
      bootstrapAttemptRef.current === attempt && bootstrapGenerationRef.current === generation
      && tokensMatchCorrelation(
        expectedToken,
        localStorage.getItem('token'),
        AUTH_CORRELATION.VERIFIED_SESSION
      )
    );

    attempt.promise = (async () => {
      try {
        // API call to get user data with all entities
        const response = await axios.get('/api/entities/user/me', {
          // Bound startup independently of public reachability and other app APIs.
          timeout: 10000,
          signal: attempt.controller.signal,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!isCurrentAttempt()) {
          return;
        }

        const { user: userData, entities } = response.data;
        logger.debug('User entity data loaded', {
          entitiesCount: response.data.entities?.length
        });

        const combinedEntities = entities.map(selectRelevantFields);
        logger.debug('Combined entities processed', { entityCount: combinedEntities.length });

        // Use userData from the response instead of relying on entity data
        if (combinedEntities.length > 0) {
          // Set state from userData
          setUsername(userData.username);
          setGlobalRouteRole(userData.role);

          localStorage.setItem('userId', userData.id);
          localStorage.setItem('username', userData.username);

          // Store legacy userPreferences for utilities that have not moved to
          // PreferenceContext yet. If an auth-page language override is pending,
          // keep that language in this legacy cache until PreferenceContext
          // serializes the DB-backed save and writes the final authoritative cache.
          const userPreferences = {
            language: resolveLegacyUserPreferenceLanguage(userData.language_preference),
            global_filter_threshold: userData.global_filter_threshold || 10,
            distance_unit: userData.distance_unit_preference || 'km',
            theme: userData.theme_preference || 'light',
            date_format: userData.date_format_preference || 'MM/DD/YYYY',
            time_format: userData.time_format_preference || '12h',

          };
          localStorage.setItem('userPreferences', JSON.stringify(userPreferences));

          setEntities(combinedEntities);
          const firstEntity = combinedEntities[0];
          setCurrentEntity(firstEntity); // Set the first entity as current
          const firstEntityRole = firstEntity.hasActiveMembership
            ? firstEntity.membershipRole
            : userData.role;
          setUserRole(firstEntityRole);
          logger.debug('Current entity selected during bootstrap');

          setEntityId(firstEntity.entityId);
          setEntityName(firstEntity.entityName);
          setEntityLogoUrl(firstEntity.entityLogoUrl);

          // Set localStorage from userData

          localStorage.setItem('entityId', firstEntity.entityId);
          localStorage.setItem('entityName', firstEntity.entityName);
          localStorage.setItem('entity_type', firstEntity.entity_type);
          localStorage.setItem('entityLogoUrl', firstEntity.entityLogoUrl || '');
          localStorage.setItem('entityLatitude', firstEntity.latitude || '');
          localStorage.setItem('entityLongitude', firstEntity.longitude || '');
          localStorage.setItem('role', firstEntityRole);
          setBootstrapStatus('ready');

        } else {
          // An authenticated user with no selectable entity needs an explicit
          // recovery choice; it is not evidence that the credential is invalid.
          setBootstrapStatus('access_denied');
        }
      } catch (error) {
        if (!isCurrentAttempt() || error.code === 'ERR_CANCELED') return;
        logger.error('Failed to fetch user or entity data', {
          status: error.response?.status,
          code: error.response?.data?.code,
          message: error.message,
        });
        const code = error.response?.data?.code;
        if (['USER_ENTITY_MEMBERSHIP_REQUIRED', 'USER_ROLE_INVALID'].includes(code)) {
          setBootstrapStatus('access_denied');
        } else if (!localStorage.getItem('token')) {
          // The shared terminal-session handler already cleared the credential.
          setBootstrapStatus('idle');
        } else {
          setBootstrapStatus('retryable_error');
        }
      } finally {
        if (bootstrapAttemptRef.current === attempt) bootstrapAttemptRef.current = null;
      }
    })();
    return attempt.promise;
  }, [retireBootstrap, selectRelevantFields, token]);

  const handleTokenEstablished = useCallback((nextToken) => {
    retireBootstrap();
    setBootstrapStatus(nextToken ? 'pending' : 'idle');
    setToken(nextToken);
  }, [retireBootstrap]);

  useEffect(() => {
    const handleStorage = (event) => {
      const tokenWasRemoved = (
        (event.key === 'token' && event.oldValue && event.newValue === null)
        || (event.key === null && !localStorage.getItem('token'))
      );

      if (tokenWasRemoved) {
        clearUserData();
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [clearUserData]);

  useEffect(() => {
    if (token) {
      const timeoutId = setTimeout(() => {
        fetchUserData();
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [fetchUserData, token]);

  const handleEntityChange = (selectedEntity) => {
    logger.debug('Entity manually selected');
    if (selectedEntity.id === 'all') {
      // "All" is a global-admin context, not a real entity. Store a sentinel so
      // downstream filters can widen results without sending a fake entity ID.
      setCurrentEntity({ id: 'all', name: 'All' });
      setUserRole(globalRouteRole);
      setEntityId('All');
      setEntityName('All');
      setEntityLogoUrl('');
      localStorage.setItem('entityId', 'All');
      localStorage.setItem('entityName', 'All');
      localStorage.setItem('role', globalRouteRole); // Keep the global role for All
      localStorage.setItem('entityLogoUrl', '');
      localStorage.removeItem('entityLatitude');
      localStorage.removeItem('entityLongitude');

      // Dispatch custom event to notify PreferenceContext of entity change
      window.dispatchEvent(new CustomEvent('entityChanged', {
        detail: { entityId: 'All' }
      }));
    } else {
      
      // Use the role and other data from the selected entity directly
      // A persisted membership owns its scoped role. Only a synthetic catalogue
      // selection may use the separately retained global route role.
      const newRole = selectedEntity.hasActiveMembership
        ? selectedEntity.membershipRole
        : globalRouteRole;
  
      // Update localStorage with the selected entity's data
      localStorage.setItem('role', newRole);
      localStorage.setItem('entityId', selectedEntity.entityId);
      localStorage.setItem('entityName', selectedEntity.entityName);
      localStorage.setItem('entity_type', selectedEntity.entity_type);
      localStorage.setItem('entityLogoUrl', selectedEntity.entityLogoUrl || '');
      localStorage.setItem('entityLatitude', selectedEntity.latitude || '');
      localStorage.setItem('entityLongitude', selectedEntity.longitude || '');

      // Update state for Header component
      setUserRole(newRole);
      setEntityId(selectedEntity.entityId);
      setEntityName(selectedEntity.entityName);
      setEntityLogoUrl(selectedEntity.entityLogoUrl);

      // Dispatch custom event to notify PreferenceContext of entity change
      window.dispatchEvent(new CustomEvent('entityChanged', {
        detail: { entityId: selectedEntity.entityId }
      }));
  
      // Preserve membership provenance and personal preference capability when
      // publishing the selected row.
      setCurrentEntity({ ...selectedEntity, userRole: newRole });
  
    }
  };

  const handleEntityPreferenceSuccess = useCallback((targetEntityId, values, options = {}) => {
    const targetId = String(targetEntityId);

    if (options.primaryTransition) {
      setEntities(previous => previous.map(entity => ({
        ...entity,
        is_primary: String(entity.entityId) === targetId
      })));
      setCurrentEntity(previous => previous ? {
        ...previous,
        is_primary: String(previous.entityId) === targetId
      } : previous);
      return;
    }

    setEntities(previous => previous.map(entity => (
      String(entity.entityId) === targetId ? { ...entity, ...values } : entity
    )));
    setCurrentEntity(previous => (
      previous && String(previous.entityId) === targetId
        ? { ...previous, ...values }
        : previous
    ));
  }, []);

  const {
    preferenceScope,
    settingsScope,
    globalPreferenceCarrierScope
  } = useMemo(
    () => derivePreferenceScopes(currentEntity, entities),
    [currentEntity, entities]
  );

  if (!ready) return <><ServiceAvailabilityNotice /><div>{t('loading')}</div></>;

  if (token && ['pending', 'retryable_error'].includes(bootstrapStatus)) {
    const pending = bootstrapStatus === 'pending';
    const publicTitle = availabilityTitle(availability);
    const title = publicTitle === 'availability.unavailable' ? 'startup.unavailableTitle'
      : publicTitle && publicTitle !== 'availability.lastReported' ? publicTitle
        : pending ? 'startup.connectingTitle' : 'startup.failedTitle';
    const message = publicTitle === 'availability.maintenance' ? null
      : publicTitle === 'availability.offline' ? 'startup.offlineMessage'
        : publicTitle === 'availability.unavailable' ? 'startup.unavailableMessage'
          : pending ? 'startup.connectingMessage' : 'startup.failedMessage';
    // Only this explicit action retries the account read. Public recovery changes
    // explanation, never access; a slow public check cannot delay account success.
    const retry = () => { void fetchUserData(); void availability?.retry(); };
    return <main className="min-h-dvh flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
      <section aria-labelledby="startup-title" className="w-full max-w-md space-y-6 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow">
        <div role="status" aria-live="polite" className="space-y-3 break-words text-gray-700 dark:text-gray-300">
          <h1 id="startup-title" className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t(title)}</h1>
          {message && <p>{t(message)}</p>}
          <ServiceAvailabilityDetails maintenance={availability?.maintenance} title={title} />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" disabled={pending} onClick={retry} className="inline-flex items-center gap-2 rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-60">
            {pending && <span aria-hidden="true" className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
            {t(pending ? 'startup.connectingAction' : 'startup.retry')}
          </button>
          <ServiceAvailabilityStatusLink />
        </div>
      </section>
    </main>;
  }

  if (token && bootstrapStatus === 'access_denied') {
    return (<>
      <ServiceAvailabilityNotice />
      <div className="min-h-[calc(100dvh-var(--service-availability-height,0px))] flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <div className="w-full max-w-md rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow">
          <h1 className="text-xl font-semibold mb-2">
            {t('authBootstrapAccessTitle')}
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            {t('authBootstrapAccessMessage')}
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={fetchUserData}
              className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
            >
              {t('refresh')}
            </button>
            <button
              type="button"
              onClick={() => handleLogout('user_logout')}
              className="px-4 py-2 rounded border border-gray-300 dark:border-gray-600"
            >
              {t('logout')}
            </button>
          </div>
        </div>
      </div>
    </>);
  }

  return (<>
    {/* Keep notice/provider positions stable so outage updates preserve forms. */}
    <ServiceAvailabilityNotice />
    <PreferenceProvider
      scope={preferenceScope}
      settingsScope={settingsScope}
      globalPreferenceCarrierScope={globalPreferenceCarrierScope}
    >
      <ThemeProvider>
        <Router>
          <SystemAnnouncementsProvider sessionKey={token && bootstrapStatus === 'ready' ? announcementSessionKey(token) : null} currentEntity={currentEntity} entities={entities}>
          <PreferenceLoader
            token={token}
            scope={preferenceScope}
            settingsScope={settingsScope}
            globalPreferenceCarrierScope={globalPreferenceCarrierScope}
          />
          <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
          {token && (
            <Header 
              setToken={() => clearUserData()} 
              userRole={userRole}
              globalRouteRole={globalRouteRole}
              setUserRole={setUserRole} 
              username={username} 
              entities={entities}
              currentEntity={currentEntity}            
              setCurrentEntity={handleEntityChange}
              entityId={entityId}
              entityName={entityName}            
              entityLogoUrl={entityLogoUrl}
            />
          )}
          <div
            className={`bg-gray-50 dark:bg-gray-900 min-h-screen app-shell-content ${token ? 'authenticated-mobile-shell' : ''}`}
            style={{ paddingTop: token ? '54px' : '0' }}
          >
            <AnnouncementBanner />
            <Routes>
            <Route path="/login" element={!token ? <Login setToken={handleTokenEstablished} /> : <Navigate to="/" />} />
              <Route path="/register/:inviteToken" element={!token ? <InviteRegistration setToken={handleTokenEstablished} /> : <Navigate to="/" />} />
              <Route path="/forgot-password" element={!token ? <ForgotPassword /> : <Navigate to="/" />} />
              <Route path="/reset-password" element={!token ? <ResetPasswordFromLink /> : <Navigate to="/" />} />              
              <Route path="/feedback-surveys" element={<FeedbackSurveys />} />
              <Route path="/" element={token ? <Navigate to={resolveSelectedEntityLaunchPath(currentEntity, globalRouteRole)} /> : <Navigate to="/login" />} />
              <Route path="/bookings" element={token ? <BookingManagement userRole={userRole} currentEntity={currentEntity} /> : <Navigate to="/login" />} />
              <Route path="/announcements/:announcementId?" element={token ? <AnnouncementInbox canManage={isSystemAdmin(globalRouteRole)} canManageEntity={isAdmin(userRole)} /> : <Navigate to="/login" />} />
              <Route path="/system-announcements" element={token && isSystemAdmin(globalRouteRole) ? <SystemAnnouncementManager /> : <Navigate to={token ? '/' : '/login'} />} />
              <Route path="/entity-announcements" element={token && isAdmin(userRole) && Number(entityId) > 0 ? <SystemAnnouncementManager entity /> : <Navigate to={token ? '/' : '/login'} />} />
              {/* Operations is shared by driver-level roles; the page itself
                  keeps route/shuttle administration separate from driver tools. */}
              <Route path="/operations" element={token && isDriver(userRole) ? <OperationsPage currentEntity={currentEntity} userRole={userRole} /> : <Navigate to={token ? "/" : "/login"} />} />
             
              <Route path="/trips" element={token ? <TripManagement currentEntity={currentEntity} /> : <Navigate to="/login" />} />
              <Route path="/audit" element={token && isAdmin(userRole) ? <AuditLogViewer currentEntity={currentEntity} /> : <Navigate to="/" />} />            
              <Route path="/vehicles" element={token && isAdmin(userRole) ? <VehicleList currentEntity={currentEntity} /> : <Navigate to="/" />} />
              <Route path="/routes" element={token && isAdmin(userRole) ? <RouteManagement currentEntity={currentEntity} /> : <Navigate to="/" />} />
              <Route path="/schedule-management" element={token && isAdmin(userRole) ? <ScheduleManagement  currentEntity={currentEntity} /> : <Navigate to="/" />} />
              <Route path="/gps-devices" element={token && isAdmin(userRole) ? <GPSDeviceManagement currentEntity={currentEntity} userRole={userRole} /> : <Navigate to="/" />} />
              <Route path="/settings/*" element={<SettingsRoute authToken={token} userRole={userRole} entities={entities} currentEntity={currentEntity} onEntityPreferenceSuccess={handleEntityPreferenceSuccess} />} />
              <Route path="*" element={<Navigate to={token ? "/" : "/login"} />} />
            </Routes>
          </div>
          <ToastContainer
            position="bottom-left" //"top-right" "top-right", "top-center", "bottom-left" "bottom-center", "bottom-right"
            className={token ? 'mobile-toast-offset' : undefined}
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
          <WebSocketNotificationToastContainer isAuthenticated={Boolean(token)} />
          </div>
          </SystemAnnouncementsProvider>
        </Router>
      </ThemeProvider>
    </PreferenceProvider>
  </>);
}

export default App;
